import { bearerHeader } from './tokens'

export const PHASE2_FEATURES = [
  'SELECTION_ANALYTICS', 'ALIBABA1688', 'AUTO_PROMOTION_REMOVAL',
  'ADVANCED_MESSAGING', 'SALES_SCREEN',
] as const
export type Phase2Feature = typeof PHASE2_FEATURES[number]
export type Phase2FeatureState = Record<Phase2Feature, boolean>

const closed = (): Phase2FeatureState => Object.fromEntries(
  PHASE2_FEATURES.map(feature => [feature, false]),
) as Phase2FeatureState

let cached: { subject: string; authorization: string; expiresAt: number; state: Phase2FeatureState } | null = null
let cacheEpoch = 0

export async function loadPhase2Features(subjectType: string | null | undefined,
  onUnavailable?: () => void): Promise<Phase2FeatureState> {
  if (subjectType !== 'TENANT' && subjectType !== 'OPERATOR') return closed()
  const headers = bearerHeader()
  const authorization = headers.Authorization
  if (!authorization) { resetPhase2FeatureCache(); return closed() }
  // Memory only: a role is not an identity. Never reuse another login's capabilities.
  if (cached?.subject === subjectType && cached.authorization === authorization && cached.expiresAt > Date.now()) return { ...cached.state }
  const epoch = cacheEpoch
  const unavailable = () => {
    if (epoch === cacheEpoch && bearerHeader().Authorization === authorization) onUnavailable?.()
    return closed()
  }
  try {
    const path = subjectType === 'OPERATOR' ? '/ops/v1/phase2/features' : '/api/v1/phase2/features'
    const response = await fetch(path, { headers, signal: AbortSignal.timeout(8000) })
    const envelope = await response.json() as {
      code: number; data: Array<{ feature: string; enabled: boolean }> | null
    }
    if (!response.ok || envelope.code !== 0 || !Array.isArray(envelope.data)) return unavailable()
    const rows = envelope.data
    if (rows.some(row => typeof row?.feature !== 'string' || typeof row?.enabled !== 'boolean')) return unavailable()
    const keys = rows.map(row => row.feature)
    if (rows.length !== PHASE2_FEATURES.length
      || new Set(keys).size !== PHASE2_FEATURES.length
      || PHASE2_FEATURES.some(feature => !keys.includes(feature))) return unavailable()
    const state = closed()
    for (const row of rows) state[row.feature as Phase2Feature] = row.enabled === true
    if (epoch !== cacheEpoch || bearerHeader().Authorization !== authorization) return closed()
    cached = { subject: subjectType, authorization, expiresAt: Date.now() + 30_000, state }
    return state
  } catch { return unavailable() }
}

export function resetPhase2FeatureCache(): void { cached = null; ++cacheEpoch }
